import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { track } from "../utils/analytics";
import "../styles/Header.css";

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [showHeader, setShowHeader] = useState(true);

  const lastY = useRef(0);
  const location = useLocation();

  const handleNavClick = (label, destination) => {
    track("navigation_click", {
      location: "header",
      nav_item: label,
      destination,
      menu_state: isMenuOpen ? "mobile" : "desktop",
    });
    setIsMenuOpen(false);
    setPricingOpen(false);
  };

  const toggleMenu = () => {
    const next = !isMenuOpen;
    track("menu_toggle", {
      location: "header",
      menu: "mobile_nav",
      state: next ? "open" : "close",
    });
    setIsMenuOpen(next);
    if (!next) setPricingOpen(false);
  };

  const togglePricing = () => {
    const next = !pricingOpen;
    track("dropdown_toggle", {
      location: "mobile_drawer",
      dropdown: "pricing",
      state: next ? "open" : "close",
    });
    setPricingOpen(next);
  };

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 0);
      }
    }
  }, [location]);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      if (Math.abs(y - lastY.current) < 10) return;
      if (y > lastY.current && y > 120) {
        setShowHeader(false);
      } else {
        setShowHeader(true);
      }
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = isMenuOpen ? "hidden" : original || "";
    return () => {
      document.body.style.overflow = original || "";
    };
  }, [isMenuOpen]);

  return (
    <>
      <header className={`header-container ${showHeader ? "visible" : "hidden"}`}>
        <div className="header-nav-container">
          <h1 className="logo">
            <Link to="/" onClick={() => handleNavClick("logo", "/")}>
              BestDay2Marry
            </Link>
          </h1>

          <nav className="bestday2marry-nav" aria-label="Primary">
            <Link to="/#about" onClick={() => handleNavClick("About", "/#about")}>
              About
            </Link>
            <Link
              to="/#products"
              onClick={() => handleNavClick("Products", "/#products")}
            >
              Products
            </Link>
            <Link
              to="/#locations"
              onClick={() => handleNavClick("Locations", "/#locations")}
            >
              Our&nbsp;Locations
            </Link>
            <Link
              to="/how_it_works"
              onClick={() => handleNavClick("How It Works", "/how_it_works")}
            >
              How&nbsp;It&nbsp;Works
            </Link>

            <div
              className="dropdown"
              onMouseEnter={() =>
                track("dropdown_open", { location: "header", dropdown: "pricing" })
              }
            >
              <Link
                to="/pricing/businesses"
                onClick={() => handleNavClick("Pricing", "/pricing/businesses")}
              >
                Pricing
              </Link>
              <div className="dropdown-content" role="menu">
                <Link
                  to="/pricing/businesses"
                  onClick={() =>
                    handleNavClick("Pricing – Businesses", "/pricing/businesses")
                  }
                  role="menuitem"
                >
                  Businesses
                </Link>
                <Link
                  to="/pricing/couples"
                  onClick={() =>
                    handleNavClick("Pricing – Couples", "/pricing/couples")
                  }
                  role="menuitem"
                >
                  Couples
                </Link>
              </div>
            </div>

            <Link to="/signin" onClick={() => handleNavClick("Sign In", "/signin")}>
              Sign&nbsp;In
            </Link>
          </nav>

          <button
            className={`hamburger ${isMenuOpen ? "active" : ""}`}
            onClick={toggleMenu}
            aria-label="Toggle navigation"
            aria-expanded={isMenuOpen}
            aria-controls="mobile-drawer"
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </header>

      {/* Outside header to avoid stacking-context issues */}
      <div
        className={`nav-overlay ${isMenuOpen ? "show" : ""}`}
        onClick={toggleMenu}
      />
      <aside
        id="mobile-drawer"
        className={`mobile-drawer ${isMenuOpen ? "open" : ""}`}
        aria-hidden={!isMenuOpen}
        aria-label="Mobile navigation"
      >
        <button className="drawer-close" aria-label="Close menu" onClick={toggleMenu}>
          ×
        </button>

        <ul className="drawer-list">
          <li>
            <Link to="/#about" onClick={() => handleNavClick("About", "/#about")}>
              About
            </Link>
          </li>
          <li>
            <Link
              to="/#products"
              onClick={() => handleNavClick("Products", "/#products")}
            >
              Products
            </Link>
          </li>
          <li>
            <Link
              to="/#locations"
              onClick={() => handleNavClick("Locations", "/#locations")}
            >
              Our Locations
            </Link>
          </li>
          <li>
            <Link
              to="/how_it_works"
              onClick={() => handleNavClick("How It Works", "/how_it_works")}
            >
              How It Works
            </Link>
          </li>

          <li className={`drawer-section ${pricingOpen ? "open" : ""}`}>
            <button
              className="submenu-toggle"
              onClick={togglePricing}
              aria-expanded={pricingOpen}
              aria-controls="pricing-submenu"
            >
              Pricing
            </button>
            <ul id="pricing-submenu" className="submenu">
              <li>
                <Link
                  to="/pricing/businesses"
                  onClick={() =>
                    handleNavClick("Pricing – Businesses", "/pricing/businesses")
                  }
                >
                  Businesses
                </Link>
              </li>
              <li>
                <Link
                  to="/pricing/couples"
                  onClick={() =>
                    handleNavClick("Pricing – Couples", "/pricing/couples")
                  }
                >
                  Couples
                </Link>
              </li>
            </ul>
          </li>

          <li className="drawer-divider" />

          <li>
            <Link to="/signin" onClick={() => handleNavClick("Sign In", "/signin")}>
              Sign In
            </Link>
          </li>
        </ul>
      </aside>
    </>
  );
}

export default Header;
